import { NgIf } from "@angular/common";
import { Component, OnInit } from "@angular/core";

import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { ActivatedRoute, Router } from "@angular/router";
import { AppStateService, FirebaseAuthService, RequestService } from "@services/index";

const RESEND_COOLDOWN_SECONDS = 30;

@Component({
  selector: "fh-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.css"],
  standalone: true,
  imports: [MatButtonModule, MatIconModule, FormsModule, MatInputModule, NgIf],
})
export class LoginComponent implements OnInit {
  email = "";
  emailErr = "";
  code = "";
  codeErr = "";
  authEror = "";
  infoMessage = "";
  showOTP = false;
  isSubmitting = false;
  googleSignInPending = false;
  resendAvailableInSeconds = 0;
  private resendTimer: ReturnType<typeof setInterval> | undefined;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private auth: RequestService,
    private appStateService: AppStateService,
    private firebaseAuth: FirebaseAuthService
  ) {}

  ngOnInit() {
    // Session lives in an httpOnly cookie now (can't be read from
    // localStorage), so check for an already-valid session against the
    // backend rather than trusting a client-side flag.
    this.appStateService.checkSession().subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        this.router.navigate(["/dashboard"]);
      }
    });

    // Magic-link support: a click from the verification email lands back
    // here with ?code=&email= already filled in — verify immediately.
    const params = this.route.snapshot.queryParamMap;
    const linkEmail = params.get("email");
    const linkCode = params.get("code");
    if (linkEmail && linkCode) {
      this.email = linkEmail;
      this.code = linkCode;
      this.showOTP = true;
      this.verifyCode();
    }
  }

  keyDownFunction(event: { keyCode: number }) {
    if (event.keyCode !== 13) return;
    if (this.showOTP) {
      this.verifyCode();
    } else {
      this.sendCode();
    }
  }

  sendCode() {
    this.emailErr = "";
    this.authEror = "";
    this.infoMessage = "";
    if (!this.email || !this.email.trim()) {
      this.emailErr = "Email is required.";
      return;
    }

    this.isSubmitting = true;
    this.auth.post("/api/v1/auth/send-verification-code", { email: this.email }).subscribe({
      next: (res: any) => {
        this.isSubmitting = false;
        if (res.status === true) {
          this.showOTP = true;
          this.startResendCooldown();
        } else {
          if (res.data?.retryAfterSeconds) {
            this.showOTP = true;
            this.startResendCooldown(res.data.retryAfterSeconds);
          }
          this.authEror = res.message || "Failed to send verification code.";
        }
      },
      error: () => {
        this.isSubmitting = false;
        this.authEror = "Something went wrong. Please try again.";
      },
    });
  }

  resendCode() {
    if (this.resendAvailableInSeconds > 0) return;
    this.sendCode();
  }

  changeEmail() {
    this.showOTP = false;
    this.code = "";
    this.codeErr = "";
    this.authEror = "";
    this.infoMessage = "";
  }

  verifyCode() {
    this.codeErr = "";
    this.authEror = "";
    this.infoMessage = "";
    if (!this.code || !this.code.trim()) {
      this.codeErr = "Verification code is required.";
      return;
    }

    this.isSubmitting = true;
    this.auth.post("/api/v1/auth/verify-code", { email: this.email, code: this.code }).subscribe({
      next: (res: any) => {
        this.isSubmitting = false;
        if (res.status === true) {
          this.hydrateSessionAndNavigate();
        } else if (res.data?.pendingActivation) {
          this.infoMessage =
            res.message ||
            "Account created — pending activation. An existing admin needs to activate it before you can sign in.";
        } else if (res.data?.reason === "admin_access_required") {
          this.router.navigate(["/request-admin-access"], { state: { email: this.email } });
        } else {
          this.authEror = res.message || "Invalid verification code.";
        }
      },
      error: () => {
        this.isSubmitting = false;
        this.authEror = "Something went wrong. Please try again.";
      },
    });
  }

  async signInWithGoogle() {
    this.authEror = "";
    this.infoMessage = "";
    this.googleSignInPending = true;
    let idToken: string;
    let email: string | null;
    try {
      ({ idToken, email } = await this.firebaseAuth.signInWithGoogleIdToken());
    } catch (_error) {
      this.googleSignInPending = false;
      this.authEror = "Google sign-in was cancelled or failed.";
      return;
    }

    this.auth.post("/api/v1/auth/google-login", { idToken }).subscribe({
      next: (res: any) => {
        this.googleSignInPending = false;
        if (res.status === true) {
          this.hydrateSessionAndNavigate();
        } else if (res.data?.pendingActivation) {
          this.infoMessage =
            res.message ||
            "Account created — pending activation. An existing admin needs to activate it before you can sign in.";
        } else if (res.data?.reason === "admin_access_required") {
          // The Google account is real, it's just not an admin (and self-service
          // login never grants admin access to an account that already exists as
          // something else) — send them to a dedicated page rather than showing
          // this as a plain login error.
          this.router.navigate(["/request-admin-access"], { state: { email } });
        } else {
          this.authEror = res.message || "Google sign-in failed.";
        }
      },
      error: () => {
        this.googleSignInPending = false;
        this.authEror = "Something went wrong. Please try again.";
      },
    });
  }

  private startResendCooldown(seconds: number = RESEND_COOLDOWN_SECONDS) {
    this.resendAvailableInSeconds = Math.max(1, Math.ceil(seconds));
    if (this.resendTimer) clearInterval(this.resendTimer);
    this.resendTimer = setInterval(() => {
      this.resendAvailableInSeconds -= 1;
      if (this.resendAvailableInSeconds <= 0 && this.resendTimer) {
        clearInterval(this.resendTimer);
      }
    }, 1000);
  }

  private hydrateSessionAndNavigate() {
    this.appStateService.checkSession().subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        this.router.navigate(["/dashboard"]);
      } else {
        this.authEror = "Login succeeded but the session could not be established.";
      }
    });
  }
}
